import { useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { QuantityInput } from '../../components/Form/QuantityInput';
import { Img } from '../../components/Img/Img';
import { useLoop54ProductsByIds } from '../../hooks/useLoop54ProductsByIds';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { useCart } from '../../state/cart';
import { ApiOrderItem } from '../../types';
import { Button, ButtonLink } from '../Form/Button';
import { Main } from '../Main/Main';
import { Routing } from '../Routes';
import { clone, sum } from '../utils';
import { LoaderPage } from './LoaderPage';
import './ReorderPage.css';

/**
 * @displayOrderItems combine data from @orderData and @freshProductsData,
 * and the @freshProductsData is dependent on @orderData.
 *
 * TODO: refactor this mess.
 */

type UrlParams = { orderId: string };
type AugumentedOrderItem = ApiOrderItem & {
	product: { price: number; perma: string };
};

export const Reorder = (): JSX.Element => {
	let [displayOrderItems, setDisplayOrderItems] = useState<AugumentedOrderItem[]>([]);
	let [freshOrdersFetched, setFreshOrdersFetched] = useState(false);
	let [ordersAddedToCart, setOrdersAddedToCart] = useState(false);
	const locationState = useLocation<{ displayId: string }>().state;
	const { orderId } = useParams<UrlParams>();
	const { cart, setQuantities } = useCart();
	const { data: orderData, error } = useApi(Res.order, { orderId });
	const productIds = orderData ? orderData.items.map((e) => e.product.id) : [];
	// TODO page-handling here?
	const { data: freshProductsData } = useLoop54ProductsByIds(productIds);

	const { asPrice } = useFormatting();
	const t = useTranslation().orderPages;
	const tRoutes = useTranslation().routes;

	const totalSum = sum((displayOrderItems ?? []).map((e) => (e.product.price || e.product.finalPrice) * e.quantity));

	if (error)
		return (
			<Main>
				<p>{t.couldNotFindOrderWithId}</p>
			</Main>
		);

	if (!orderData) return <LoaderPage />;

	if (freshProductsData && !freshOrdersFetched) {
		// Update some values for displayed order items.
		let displayProducts: AugumentedOrderItem[] = [];
		for (let i = 0; i < orderData.items.length; i++) {
			let orderItem = orderData.items[i];
			let displayOrderItem = clone(orderItem) as AugumentedOrderItem;
			for (let j = 0; j < freshProductsData.results.items.length; j++) {
				let freshProduct = freshProductsData.results.items[j];
				if (freshProduct.id === orderItem.product.id) {
					displayOrderItem.quantity = orderItem.quantity;
					displayOrderItem.product.price = freshProduct.price;
					displayOrderItem.product.perma = freshProduct.perma;
				}
			}
			displayProducts.push(displayOrderItem);
		}
		setDisplayOrderItems(displayProducts);
		setFreshOrdersFetched(true);
	}

	const { id } = orderData;

	const handleChange = (quantity: number, productId: number) => {
		const newDisplayOrderItems = displayOrderItems.map((displayOrderItem) => {
			if (displayOrderItem.product.id === productId) {
				displayOrderItem.quantity = quantity || 0;
			}

			return displayOrderItem;
		});

		setDisplayOrderItems(newDisplayOrderItems);
	};

	const handleAddToCart = () => {
		let overlappingItemIds: number[] = [];
		const newCartItems: { id: number; quantity: number }[] = [];
		let id: number, quantity: number;
		displayOrderItems.forEach((displayOrderItem) => {
			if (displayOrderItem.quantity === 0) return;
			id = displayOrderItem.product.id;
			quantity = displayOrderItem.quantity;
			cart?.items.forEach((cartItem) => {
				if (cartItem.product.id === displayOrderItem.product.id) {
					quantity += cartItem.quantity;
					overlappingItemIds.push(id);
				}
			});
			newCartItems.push({ id, quantity });
		});

		// Extract all items from cart that's not in the displayOrderItem list.
		const filteredCartItems: { id: number; quantity: number }[] = [];
		cart?.items.forEach((cartItem) => {
			if (!overlappingItemIds.includes(cartItem.product.id)) {
				filteredCartItems.push({
					id: cartItem.product.id,
					quantity: cartItem.quantity,
				});
			}
		});

		setQuantities([...newCartItems, ...filteredCartItems]);
		setOrdersAddedToCart(true);
	};

	if (ordersAddedToCart) {
		return (
			<Main>
				<h1 className="big">
					{t.reorder} {locationState ? locationState.displayId : id}
				</h1>
				<p>{t.yourProductsHaveBeenAdded}</p>
				<ButtonLink linkTo={tRoutes.checkout} className="big">
					{t.goToCheckout}
				</ButtonLink>
			</Main>
		);
	}

	return (
		<Main>
			<h1 className="big">
				{t.reorder} {locationState ? locationState.displayId : id}
			</h1>
			<ReorderTable products={displayOrderItems} handleQuantityChange={handleChange} />
			<p>
				{t.productSum}: {asPrice(totalSum)}
			</p>
			<Button onClick={handleAddToCart}>{t.addToCart}</Button>
		</Main>
	);
};

export const reorderRouting: Routing = {
	path(urls) {
		return `${urls.reorder}/:orderId`;
	},
	component: Reorder,
	requiresAuth: true,
};

type ReorderTableType = {
	products: AugumentedOrderItem[];
	handleQuantityChange: (quantity: number, productId: number) => void;
};
const ReorderTable = ({ products, handleQuantityChange }: ReorderTableType): JSX.Element => {
	const t = useTranslation().previouslyBoughtPage;

	return (
		<>
			<div className="bought-products-headings">
				<p className="product">{t.product}</p>
				<p className="item-no not-mobile">{t.itemNo}</p>
				<p className="price">{t.price}</p>
				<p className="total not-mobile">{t.total}</p>
				<p className="amount">{t.amount}</p>
			</div>
			{products.map((product) => (
				<PreviouslyBoughtProductRow
					key={product.id}
					product={product}
					handleQuantityChange={handleQuantityChange}
				/>
			))}
		</>
	);
};

type PreviouslyBoughtProductType = {
	product: AugumentedOrderItem;
	handleQuantityChange: (quantity: number, productId: number) => void;
};
const PreviouslyBoughtProductRow = ({
	product: orderItem,
	handleQuantityChange,
}: PreviouslyBoughtProductType): JSX.Element => {
	const { asPrice } = useFormatting();
	const imgRef = useRef<HTMLImageElement>(null);

	const productImg = <Img ref={imgRef} src={orderItem.image} alt={orderItem.product.name} width={50} height={50} />;

	return (
		<>
			<div className="bought-products-row">
				<div className="image">
					<Link to={'/-p-' + orderItem.product.id}>{productImg}</Link>
				</div>
				<div className="name">
					<Link to={'/-p-' + orderItem.product.id}>{orderItem.product.name}</Link>
				</div>
				<div className="item-no not-mobile">{orderItem.product.displayId}</div>
				<div className="price not-mobile">
					{asPrice(orderItem.product.price ?? orderItem.product.finalPrice)}
				</div>
				<div className="total">{asPrice(orderItem.product.finalPrice)}</div>

				<div className="quantity">
					<QuantityInput
						defaultValue={orderItem.quantity}
						onBlur={(value) => handleQuantityChange(value, orderItem.product.id)}
						step={orderItem.product.bundleSize}
						iconSize={17}
						allowZero
					/>
				</div>
			</div>
		</>
	);
};
